import { colors } from "@mui/material"
import {
  SimplePaletteColorOptions,
  experimental_extendTheme as extendTheme,
} from "@mui/material/styles"

import { NumberFont, TitleFont } from "./fonts"

declare module "@mui/material/Dialog" {
  interface DialogProps {
    position?: "top"
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    accent: true
  }
}

declare module "@mui/material/TextField" {
  interface TextFieldPropsColorOverrides {
    accent: true
  }
}

declare module "@mui/material/Slider" {
  interface SliderPropsColorOverrides {
    accent: true
  }
}

declare module "@mui/material/LinearProgress" {
  interface LinearProgressPropsColorOverrides {
    accent: true
  }
}

declare module "@mui/material" {
  interface Palette {
    accent: SimplePaletteColorOptions
    paperAlt: string
    paperAltSelected: string
    red: string
    blue: string
    green: string
    lime: string
    yellow: string
    purple: string
    indigo: string
    cyan: string
    pink: string
    orange: string
  }

  interface PaletteOptions {
    accent: SimplePaletteColorOptions
    paperAlt: string
    paperAltSelected: string
    red: string
    blue: string
    green: string
    lime: string
    yellow: string
    purple: string
    indigo: string
    cyan: string
    pink: string
    orange: string
  }
}

declare module "@mui/material/TableCell" {
  interface TableCellPropsVariantOverrides {
    number: true
    text: true
  }
}

const boxShadow = "0px 4px 57.7px 0px rgba(255, 255, 255, 0.12)"

export const theme = extendTheme({
  colorSchemes: {
    dark: {
      palette: {
        paperAlt: "rgba(6, 13, 10, 1)",
        paperAltSelected: "rgba(33, 33, 33, 1)",
        success: {
          main: "rgba(0, 255, 42, 1)",
        },
        text: {
          secondary: "rgba(159, 159, 159)",
        },
        primary: {
          main: "rgb(250,250,250)",
        },
        secondary: {
          main: "rgb(170,170,170)",
        },
        accent: {
          main: "rgba(0, 255, 42, 1)",
        },
        TableCell: {
          border: "rgba(24, 25, 26, 1)",
          // border: "rgba(255, 255, 255, 0.12)", // same as divider
        },
        background: {
          paper: "rgba(22, 22, 24, 1)",
          default: "rgba(14, 14, 15, 1)",
        },
        red: colors.red[500],
        blue: colors.blue[500],
        green: colors.green[500],
        lime: colors.lime[500],
        yellow: colors.yellow[500],
        purple: colors.purple[500],
        indigo: colors.indigo[500],
        cyan: colors.cyan[500],
        pink: colors.pink[500],
        orange: colors.orange[500],
      },
    },
    light: {
      palette: {
        paperAlt: "rgba(245, 245, 245)",
        paperAltSelected: "rgba(220, 220, 220)",
        primary: {
          main: "rgb(0,0,0)",
        },
        secondary: {
          main: "rgb(100,100,100)",
        },
        // @ts-ignore
        accent: {
          main: "rgba(76, 175, 81)",
        },
        background: {
          paper: "rgb(251, 251, 251)",
          default: "rgb(243, 243, 243)",
        },
        red: colors.red[500],
        blue: colors.blue[500],
        green: colors.green[500],
        lime: colors.lime[500],
        yellow: colors.yellow[500],
        purple: colors.purple[500],
        indigo: colors.indigo[500],
        cyan: colors.cyan[500],
        pink: colors.pink[500],
        orange: colors.orange[500],
      },
    },
  },
  typography: {
    fontWeightMedium: 400,
    fontFamily: TitleFont,
    h3: {
      fontSize: "2rem",
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 700,
    },
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme, ownerState }) => ({
          background: "var(--mui-palette-background-default)",
          padding: theme.spacing(2.5),
          ...(!ownerState.fullScreen
            ? {
                borderRadius: theme.shape.borderRadius * 2.5,
                border: "1px solid var(--mui-palette-borderSecondary)",
                boxShadow,
              }
            : {
                borderRadius: 0,
                border: "none",
              }),
          margin: 0,
          ...(ownerState.position === "top" &&
            !ownerState.fullScreen && {
              top: "100px",
              left: "50%",
              transform: "translateX(-50%)",
              position: "absolute",
            }),
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backdropFilter: "blur(6px)",
          background: "rgba(var(--mui-palette-background-paperChannel) / 0.5)",
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          boxShadow: "none !important",
          "&:active": {
            transform: "scale(0.98)",
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        component: "div",
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          "&:not(.Mui-selected)": {
            fontWeight: 400,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          marginLeft: 8,
          marginRight: 8,
        },
        indicator: {
          // borderRadius: "5px",
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        variant: "outlined",
      },
      styleOverrides: {
        root: {
          border: "none",
        },
      },
    },
    MuiTextField: {},
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: "var(--mui-palette-background-paper)",
        },
      },
    },
    MuiTableCell: {
      variants: [
        {
          props: { variant: "number" },
          style: {
            fontSize: "1rem",
            fontWeight: 700,
            fontFamily: NumberFont,
          },
        },
        {
          props: { variant: "text" },
          style: {
            fontSize: "1rem",
            fontWeight: 700,
          },
        },
      ],
      styleOverrides: {
        head: {
          fontSize: "0.875rem",
          fontWeight: 500,
          color: "var(--mui-palette-text-secondary)",
          borderBottom: "1px solid rgba(105, 105, 105, 1)",
        },
        root: {
          "tbody tr:last-of-type &": {
            borderBottom: "none",
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 10,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1460,
      xl: 1920,
    },
  },
})
